import { type NotWorthIt } from '@/types/utils';

/**
 * Get a list of refs from a list of Apollo Client Cache entities or a single
 * entity.
 */
const getArrayOrObjectRefs = (
	items: { __ref: string }[] | { __ref: string }
): string[] => {
	if (Array.isArray(items)) {
		return items
			.filter(
				(item) =>
					item && Object.prototype.hasOwnProperty.call(item, '__ref')
			)
			.map((item) => item.__ref);
	}

	if (items && Object.prototype.hasOwnProperty.call(items, '__ref')) {
		return [items.__ref];
	}

	return [];
};

/**
 * Map the data from Apollo Cache into data that should be persisted.
 *
 * Relies on our custom `persistLink` to be used when configuring Apollo Client.
 *
 * This has mostly been copied from the apollo-cache-persist examples
 * https://github.com/apollographql/apollo-cache-persist/tree/master/examples/react-native/src/utils/persistence
 */
export const persistenceMapper = async (data: string) => {
	const parsed = JSON.parse(data);

	const persistedData: Record<string, unknown> = {};
	const persistEntityKeys: string[] = [];
	const rootQuery = parsed.ROOT_QUERY;

	persistedData.ROOT_QUERY = Object.keys(rootQuery).reduce(
		(obj: NotWorthIt, key) => {
			if (key === '__typename') {
				return obj;
			}

			if (/@persist$/.test(key)) {
				obj[key] = rootQuery[key];

				persistEntityKeys.push(...getArrayOrObjectRefs(rootQuery[key]));
			}

			return obj;
		},
		{ __typename: 'Query' }
	);

	// Map keys to entities that should be persisted
	// Also make sure to recursively get the related entities of entities
	// If we don't do this recursively, this case might happen:
	//
	// Cache:
	// {
	//	ROOT_QUERY: {
	//	  MyQuery: {__ref: 'User:1'},
	//  },
	//  'User:1': {
	//    id: 1,
	//    name: 'Hello',
	//    companyAccount: {__ref: 'CompanyAccount:1'},
	//  },
	//  'CompanyAccount:1': {id: 1, name: 'My company'},
	// }
	//
	// Persisted data would be missing the 'CompanyAccount:1':
	// {
	//	ROOT_QUERY: {
	//	  MyQuery@persist: {__ref: 'User:1'},
	//  },
	//  'User:1': {
	//    id: 1,
	//    name: 'Hello',
	//    companyAccount: {__ref: 'CompanyAccount:1'},
	//  },
	// }
	//
	// So, we need to make sure that all related entities are persisted.
	while (persistEntityKeys.length > 0) {
		const key = persistEntityKeys.pop();

		// Only do continue if the entity with this key is not already added to
		// the persisted data, otherwise, we might run into an infinite loop
		if (key && !persistedData[key]) {
			const entity = parsed[key];
			persistedData[key] = entity;

			// Loop through all fields of the entity and add any related
			// objects to `persistEntityKeys` list so that we would loop
			// through them as well and add those entities to the persistedData
			Object.keys(entity).forEach((fieldKey) => {
				const children = getArrayOrObjectRefs(entity[fieldKey]);
				persistEntityKeys.push(...children);
			});
		}
	}

	return JSON.stringify(persistedData);
};
